// features/products/packageSearch.ts
//
// Tìm gói theo tên / mã cho ô tìm ở bước chọn gói (QĐ 0130, CEO góp ý
// 13/09/2026: "ô tìm tên để gợi ý cái gần giống nhất"). Logic thuần, tách khỏi
// PackageSearch.tsx để kiểm được không cần dựng giao diện.
//
// Hai vòng: (1) chứa đủ mọi chữ gõ vào (không dấu, không phân biệt hoa thường)
// — xếp tên bắt đầu bằng chuỗi lên trước; (2) chưa đủ chỗ thì nối thêm gói
// "gần giống": có một từ trong tên lệch đúng MỘT ký tự so với chữ gõ (gõ
// "mong" vẫn ra "MÔNG", gõ "hog" vẫn ra "HỒNG"). Vòng 2 đánh dấu `fuzzy` để
// giao diện tách thành nhóm riêng, không trộn với kết quả khớp thật.

/** Bỏ dấu tiếng Việt + hoa thường + gọn khoảng trắng. "Mộng Diễm" → "mong diem". */
export function normalizeText(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export type SearchableItem = {
  /** Khoá gói trên bộ chọn (option key). */
  key: string;
  /** Tên hiển thị (đã bỏ hậu tố make nếu là cặp). */
  label: string;
  /** Mã sản phẩm — cũng tìm được theo mã (sale nhớ mã hơn tên). */
  code: string;
};

export type SearchHit<T extends SearchableItem> = {
  item: T;
  /** true = vòng "gần giống" (lệch một ký tự), không phải khớp chuỗi. */
  fuzzy: boolean;
};

/** Khoảng cách sửa (Levenshtein) giữa hai chuỗi ngắn; cắt sớm khi vượt `max`. */
export function editDistance(a: string, b: string, max = 1): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let left = i;
    let rowMin = left;
    for (let j = 1; j <= b.length; j++) {
      const sub = prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1);
      const cur = Math.min(prev[j]! + 1, left + 1, sub);
      prev[j - 1] = left;
      left = cur;
      if (cur < rowMin) rowMin = cur;
    }
    prev[b.length] = left;
    if (rowMin > max) return max + 1;
  }
  return prev[b.length]!;
}

/**
 * Tìm gói theo chữ gõ. Rỗng → không gợi ý gì (ô tìm chưa dùng thì lưới lo).
 * Tối đa `limit` kết quả, khớp thật đứng trước gần giống.
 */
export function matchPackages<T extends SearchableItem>(
  query: string,
  items: readonly T[],
  limit = 8,
): SearchHit<T>[] {
  const q = normalizeText(query);
  if (!q) return [];
  const tokens = q.split(" ");

  const exact: { item: T; rank: number }[] = [];
  for (const it of items) {
    const name = normalizeText(it.label);
    const code = normalizeText(it.code);
    const hay = `${name} ${code}`;
    if (!tokens.every((t) => hay.includes(t))) continue;
    // Bắt đầu bằng chuỗi gõ → 0; mã khớp → 1; chứa ở giữa → 2.
    const rank = name.startsWith(q) ? 0 : code.startsWith(q) ? 1 : 2;
    exact.push({ item: it, rank });
  }
  exact.sort((a, b) => a.rank - b.rank);
  const out: SearchHit<T>[] = exact.slice(0, limit).map((e) => ({ item: e.item, fuzzy: false }));
  if (out.length >= limit) return out;

  // Vòng gần giống: chỉ khi chữ gõ đủ dài (≥ 3) để "lệch một ký tự" còn nghĩa.
  const seen = new Set(out.map((h) => h.item.key));
  for (const it of items) {
    if (out.length >= limit) break;
    if (seen.has(it.key)) continue;
    const words = normalizeText(it.label).split(" ");
    const near = tokens.every(
      (t) => t.length >= 3 && words.some((w) => editDistance(t, w, 1) <= 1),
    );
    if (near) {
      out.push({ item: it, fuzzy: true });
      seen.add(it.key);
    }
  }
  return out;
}
